import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import {
    BehaviorSubject,
    Observable,
    Observer,
    catchError,
    map,
    take,
    throwError,
    tap,
} from 'rxjs';
import { toast } from 'ngx-sonner';
import { composeUrl, toFormData } from 'app/core/shared/helper/url.helper';
import { PaginateResponse, Response } from 'app/model';
import {
    CDFT,
    CDFTTable,
    OptionItem,
    StringEquipment,
    WellString,
    WellStringStatus,
    CdftStatus,
} from 'app/modules/cdft/model/cdft.model';
import { StringEquipmentState } from 'app/modules/cdft/state/string-equipment.state';
import { WellStringStatusState } from 'app/modules/cdft/state/well-string-status.state';
import { first } from 'lodash';
import { DATRequest } from 'app/modules/daily-activity-tracker/model';
import { ValveOptionTypeState } from 'app/modules/cdft/state/valve-option-type.state';
import { CDFTState } from 'app/modules/cdft/state/cdft.state';
import { WellStringState } from 'app/modules/cdft/state/well-string.state';
import { CDFTOfflineService } from './cdft-offline.service';

@Injectable({ providedIn: 'root' })
export class CDFTService {
    private _wellProdCategory: BehaviorSubject<any> = new BehaviorSubject(null);
    private _cdftErrors: BehaviorSubject<any> = new BehaviorSubject(null);
    private readonly _isOnline: BehaviorSubject<boolean> = new BehaviorSubject(
        navigator.onLine,
    );
    private _isSyncing = false;

    constructor(
        private _httpClient: HttpClient,
        private stringEquipmentState: StringEquipmentState,
        private wellStringStatusState: WellStringStatusState,
        private valveOptionTypeState: ValveOptionTypeState,
        private wellStringState: WellStringState,
        private readonly state: CDFTState,
        private readonly offlineService: CDFTOfflineService,
    ) {
        // Monitor online/offline status
        globalThis.addEventListener('online', () => {
            this._isOnline.next(true);
            this.processSyncQueue();
        });
        globalThis.addEventListener('offline', () =>
            this._isOnline.next(false),
        );
    }

    get wellProdCategory$(): Observable<any> {
        return this._wellProdCategory.asObservable();
    }

    get cdftErrors$(): Observable<any> {
        return this._cdftErrors.asObservable();
    }

    get isOnline$(): Observable<boolean> {
        return this._isOnline.asObservable();
    }

    getCdftTable(
        params: any,
    ): Observable<Response<PaginateResponse<CDFTTable>>> {
        const url = composeUrl(
            '/v1/PreventiveMaintenance/Pm/GetCdftRecordList',
            params,
        );
        return new Observable((observer) => {
            // If offline, return cached data immediately without trying API
            if (!this._isOnline.value) {
                this.offlineService
                    .getCDFTTable()
                    .then((cachedData) => {
                        const processedResponse = this.processOfflineData(
                            cachedData,
                            params,
                        );
                        if (
                            (processedResponse.data &&
                                processedResponse.data.length > 0) ||
                            cachedData.length === 0
                        ) {
                            observer.next({
                                data: processedResponse,
                                message: 'Data loaded from offline cache',
                                isSuccess: true,
                                statusCode: 200,
                            } as unknown as Response<
                                PaginateResponse<CDFTTable>
                            >);
                            observer.complete();
                            return;
                        }
                        // No matching data found (but cache exists)
                        observer.next({
                            data: processedResponse,
                            message:
                                'No matching records found in offline cache',
                            isSuccess: true,
                            statusCode: 200,
                        } as unknown as Response<PaginateResponse<CDFTTable>>);
                        observer.complete();
                    })
                    .catch((err) => {
                        toast.error('Error loading cached data', {
                            position: 'top-center',
                            duration: 4000,
                            closeButton: true,
                        });
                        observer.error(err);
                    });
                return;
            }

            this._httpClient
                .get<Response<PaginateResponse<CDFTTable>>>(url)
                .subscribe(
                    (response) => {
                        // Cache the data offline
                        if (response.data) {
                            this.offlineService
                                .saveCDFTTable(response.data)
                                .catch((err) =>
                                    console.error(
                                        'Error caching CDFT table:',
                                        err,
                                    ),
                                );
                        }
                        observer.next(response);
                        observer.complete();
                    },
                    async (error) => {
                        // If offline, try to get cached data
                        if (!this._isOnline.value) {
                            try {
                                const cachedData =
                                    await this.offlineService.getCDFTTable();
                                const processedResponse =
                                    this.processOfflineData(cachedData, params);

                                observer.next({
                                    data: processedResponse,
                                    message: 'Data loaded from offline cache',
                                    isSuccess: true,
                                    statusCode: '200',
                                } as unknown as Response<
                                    PaginateResponse<CDFTTable>
                                >);
                                observer.complete();
                                return;
                            } catch (e) {
                                // Fall through to error
                            }
                        }
                        observer.error(error);
                    },
                );
        });
    }

    private processOfflineData(
        allData: CDFTTable[],
        params: any,
    ): PaginateResponse<CDFTTable> {
        let filteredData = [...allData];

        // 1. Filter
        if (params) {
            const searchTerm = params.FieldName?.toLowerCase();
            if (searchTerm) {
                filteredData = filteredData.filter(
                    (item) =>
                        item.wellLongName?.toLowerCase().includes(searchTerm) ||
                        item.platformLongName
                            ?.toLowerCase()
                            .includes(searchTerm) ||
                        item.fieldLongName
                            ?.toLowerCase()
                            .includes(searchTerm) ||
                        item.stringLongName
                            ?.toLowerCase()
                            .includes(searchTerm) ||
                        item.status?.toLowerCase().includes(searchTerm) ||
                        item.id?.toLowerCase().includes(searchTerm),
                );
            }

            // Add other specific filters if params keys match
            if (params.Platform) {
                filteredData = filteredData.filter((item) =>
                    item.platformLongName
                        ?.toLowerCase()
                        .includes(params.Platform.toLowerCase()),
                );
            }
        }

        // 2. Sort (Default to orderIndex or ID)
        // If params.OrderBy exists, logic could be added here.
        // For now, rely on default order.

        // 3. Paginate
        const pageIndex = parseInt(
            params?.PageNumber || params?.Page || '1',
            10,
        );
        const pageSize = parseInt(params?.PageSize || '20', 10);
        const totalRecordCount = filteredData.length;

        const startIndex = (pageIndex - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const pagedData = filteredData.slice(startIndex, endIndex);

        return {
            data: pagedData,
            totalPages: Math.ceil(totalRecordCount / pageSize),
            totalRecords: totalRecordCount,
            size: pageSize,
            current: pageIndex,
        } as unknown as PaginateResponse<CDFTTable>;
    }

    getCdft(id: string): Observable<Response<CDFT>> {
        const url = composeUrl(
            '/v1/PreventiveMaintenance/Pm/GetCdftRecordById',
            { id },
        );
        return new Observable((observer) => {
            // If offline, return cached data immediately without trying API
            if (!this._isOnline.value) {
                this.offlineService
                    .getCDFTDetail(id)
                    .then((cachedCdft) => {
                        if (cachedCdft) {
                            const response: Response<CDFT> = {
                                data: cachedCdft,
                                message: 'Data loaded from offline cache',
                                isSuccess: true,
                                statusCode: 200,
                            } as unknown as Response<CDFT>;
                            // Manually resolve store for cached data - use store() method
                            const resolvedData =
                                this.state.storeResolver(response);
                            this.state.store(resolvedData).catch((err) => {
                                console.error(
                                    'Error storing resolved data:',
                                    err,
                                );
                            });
                            observer.next(response);
                            observer.complete();
                            return;
                        }
                        console.error('getCdft: No data in cache for ID:', id);
                        toast.error('Record not found in offline cache', {
                            position: 'top-center',
                            duration: 4000,
                            closeButton: true,
                        });
                        observer.error(
                            new Error(
                                'No cached data available for this record',
                            ),
                        );
                    })
                    .catch((err) => {
                        console.error(
                            'getCdft: Error retrieving from cache:',
                            err,
                        );
                        toast.error('Error loading cached record', {
                            position: 'top-center',
                            duration: 4000,
                            closeButton: true,
                        });
                        observer.error(err);
                    });
                return;
            }

            this._httpClient
                .get<Response<CDFT>>(url)
                .pipe(
                    tap((response) => {
                        // Cache the CDFT detail offline
                        if (response.data) {
                            this.offlineService
                                .saveCDFTDetail(response.data)
                                .catch((err) =>
                                    console.error(
                                        'Error caching CDFT detail:',
                                        err,
                                    ),
                                );
                        }
                    }),
                    this.state.resolveStore(),
                )
                .subscribe(
                    (response) => observer.next(response),
                    async (error) => {
                        // If offline, try to get cached data
                        if (!this._isOnline.value) {
                            const cachedCdft =
                                await this.offlineService.getCDFTDetail(id);
                            if (cachedCdft) {
                                const response: Response<CDFT> = {
                                    data: cachedCdft,
                                    message: 'Data loaded from offline cache',
                                    isSuccess: true,
                                    statusCode: 200,
                                } as unknown as Response<CDFT>;
                                // Manually resolve store for cached data - use store() method
                                const resolvedData =
                                    this.state.storeResolver(response);
                                this.state.store(resolvedData).catch((err) => {
                                    console.error(
                                        'Error storing resolved data:',
                                        err,
                                    );
                                });
                                observer.next(response);
                                observer.complete();
                                return;
                            }
                            // No cache data found when API fails
                            toast.error('Record not found in offline cache', {
                                position: 'top-center',
                                duration: 4000,
                                closeButton: true,
                            });
                        }
                        observer.error(error);
                    },
                );
        });
    }

    getAttachmentImageUrl(id: string): Observable<string> {
        const url = composeUrl(`v1/PreventiveMaintenance/Pm/CdtfAttachment`, {
            id,
        });

        return this._httpClient
            .get(url, {
                responseType: 'blob',
            })
            .pipe(
                // transform blob into a usable URL string
                map((blob: Blob) => {
                    return URL.createObjectURL(blob);
                }),
            );
    }

    deleteCdftAttachment(cdftAttachmentId: string) {
        const url = composeUrl(
            `/v1/PreventiveMaintenance/Pm/DeleteCdftAttachment`,
            {
                cdftAttachmentId,
            },
        );
        return this._httpClient.delete<any>(url);
    }

    updateCdft(payload: any, cachePayload?: any): Observable<any> {
        const url = composeUrl(`/v1/PreventiveMaintenance/Pm/UpdateCdftRecord`);

        // If offline, add to sync queue instead of making HTTP request
        if (!this._isOnline.value) {
            return new Observable((observer) => {
                const payloadToStore = cachePayload || payload;

                const id =
                    cachePayload?.id ||
                    (payload instanceof FormData
                        ? payload.get('id')
                        : payload.id);
                const wellName =
                    cachePayload?.wellLongName ||
                    (payload instanceof FormData
                        ? payload.get('wellLongName')
                        : payload.wellLongName);

                this.offlineService
                    .addToSyncQueue(
                        id, // CDFT ID
                        wellName, // Well Name for display
                        undefined, // stringId not needed for CDFT update
                        url,
                        'POST',
                        payloadToStore,
                    )
                    .then(async () => {
                        // Update the cached CDFT detail immediately so UI shows latest changes
                        // Use explicitly provided cache payload if available (for FormData cases)
                        if (cachePayload?.id) {
                            await this.offlineService.saveCDFTDetail(
                                cachePayload,
                            );
                        } else if (
                            !(payload instanceof FormData) &&
                            payload?.id
                        ) {
                            await this.offlineService.saveCDFTDetail(payload);
                        }
                        // Return optimistic response
                        const payloadStatus =
                            cachePayload?.status ||
                            (payload instanceof FormData
                                ? payload.get('status')
                                : payload?.status);

                        let successMessage = wellName
                            ? `Data saved locally for ${wellName}`
                            : 'Data saved locally';

                        if (payloadStatus === CdftStatus.READY) {
                            successMessage +=
                                ' and will be submitted for review when online';
                        }

                        toast.success(successMessage);
                        observer.next({
                            data: payload,
                            message: successMessage,
                            isSuccess: true,
                            statusCode: 200,
                        });
                        observer.complete();
                    })
                    .catch((err) => {
                        console.error('Error adding to sync queue:', err);
                        observer.error(err);
                    });
            });
        }

        // If online, make normal HTTP request
        return this._httpClient.post<any>(url, payload).pipe(
            take(1),
            tap(() => {
                // On successful update, remove from sync queue if exists
                this.offlineService
                    .getSyncQueue()
                    .then((queue) => {
                        const item = queue.find(
                            (q) =>
                                q.cdftId === payload.id && q.method === 'POST',
                        );
                        if (item && item.id) {
                            this.offlineService.removeSyncQueueItem(item.id);
                        }
                    })
                    .catch((err) =>
                        console.error('Error checking sync queue:', err),
                    );
            }),
            catchError((error) => {
                this._cdftErrors.next(error.error?.errors || error.message);
                return throwError(error);
            }),
        );
    }

    approvedCdftRecord(payload: any): Observable<any> {
        const url = composeUrl(
            `/v1/PreventiveMaintenance/Pm/ApproveCdftRecord`,
        );
        return this._httpClient.post<any>(url, payload).pipe(take(1));
    }

    /**
     * Process sync queue when coming back online
     * Automatically submits pending CDFT updates
     */
    async processSyncQueue(): Promise<void> {
        if (this._isSyncing) return;
        this._isSyncing = true;

        try {
            const queue = await this.offlineService.getSyncQueue();

            if (!queue || queue.length === 0) {
                return;
            }

            // Group by CDFT ID and keep only the latest for each CDFT
            const latestByCdftId = this.getLatestSyncItems(queue);

            // Process each CDFT update
            for (const item of Object.values(latestByCdftId)) {
                if (item.payload) {
                    await this.submitSyncQueueItem(item);
                }
            }
        } catch (error) {
            console.error('Error processing sync queue:', error);
        } finally {
            this._isSyncing = false;
        }
    }

    /**
     * Get latest sync queue items grouped by CDFT ID
     */
    private getLatestSyncItems(queue: any[]): { [key: string]: any } {
        const latestByCdftId: { [key: string]: any } = {};
        for (const item of queue) {
            if (item.cdftId) {
                if (
                    !latestByCdftId[item.cdftId] ||
                    item.timestamp > latestByCdftId[item.cdftId].timestamp
                ) {
                    latestByCdftId[item.cdftId] = item;
                }
            }
        }
        return latestByCdftId;
    }

    /**
     * Reconstruct FormData from serialized payload
     */
    private reconstructFormData(serializedPayload: any): FormData {
        const payloadWithoutFiles = this.removeFileMetadata(serializedPayload);
        return toFormData(payloadWithoutFiles);
    }

    private removeFileMetadata(obj: any): any {
        if (
            !obj ||
            typeof obj !== 'object' ||
            obj instanceof Date ||
            obj instanceof File
        ) {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj
                .map((item) => this.removeFileMetadata(item))
                .filter((item) => item !== undefined);
        }

        const result: any = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const value = obj[key];
                if (
                    value &&
                    typeof value === 'object' &&
                    !Array.isArray(value) &&
                    value.type === 'file'
                ) {
                    continue; // Skip file metadata
                }
                result[key] = this.removeFileMetadata(value);
            }
        }
        return result;
    }

    /**
     * Submit a single sync queue item
     */
    private async submitSyncQueueItem(item: any): Promise<void> {
        try {
            // Reconstruct FormData if payload was serialized
            const submitPayload =
                item.payload &&
                typeof item.payload === 'object' &&
                !Array.isArray(item.payload) &&
                item.payload.constructor === Object
                    ? this.reconstructFormData(item.payload)
                    : item.payload;

            await this._httpClient
                .post<any>(item.apiEndpoint, submitPayload)
                .pipe(take(1))
                .toPromise();

            // Remove from sync queue after successful submission
            if (item.id) {
                await this.offlineService.removeSyncQueueItem(item.id);
            }
        } catch (error) {
            console.error('Error submitting sync queue item:', item.id, error);

            // Update error in sync queue but keep the item for retry
            if (item.id) {
                const errorMsg =
                    error instanceof Error ? error.message : 'Unknown error';
                await this.offlineService.updateSyncQueueItemError(
                    item.id,
                    errorMsg,
                );
            }
        }
    }

    updateDailyActivityTracker(payload: DATRequest): Observable<any> {
        const url = composeUrl(`/v1/DailyActivityTracker/UpdatePmRegister`);
        return this._httpClient.post<any>(url, payload);
    }

    getStringEquipment(stringId: string) {
        const url = composeUrl(
            `/v1/EquipmentRegister/EquipmentRegister/GetEquipmentRegister/${stringId}`,
        );

        // If offline, return cached data immediately
        if (!this._isOnline.value) {
            return new Observable<Response<StringEquipment>>((observer) => {
                this.offlineService.getStringEquipment(stringId).then(
                    (cached) =>
                        this.handleCachedStringEquipment(cached, observer),
                    (err) => observer.error(err),
                );
            });
        }

        return this._httpClient.get<Response<StringEquipment>>(url).pipe(
            tap((response) => {
                if (response.data) {
                    this.offlineService
                        .saveStringEquipment(stringId, response.data)
                        .catch((err) =>
                            console.error(
                                'Error caching string equipment:',
                                err,
                            ),
                        );
                }
            }),
            this.stringEquipmentState.resolveStore((response) => {
                return response.data;
            }),
            catchError((error) => {
                if (!this._isOnline.value) {
                    return this.getStringEquipmentOfflineFallback(
                        stringId,
                        error,
                    );
                }
                return throwError(error);
            }),
        );
    }

    private handleCachedStringEquipment(
        cached: StringEquipment | undefined,
        observer: Observer<Response<StringEquipment>>,
    ) {
        if (cached) {
            const response: Response<StringEquipment> = {
                data: cached,
                isSuccess: true,
                message: 'Loaded from cache',
                statusCode: '200',
            } as unknown as Response<StringEquipment>;
            // Resolve the stringEquipmentState with cached data
            this.stringEquipmentState.store(cached).catch((err) => {
                console.error('Error storing string equipment:', err);
            });
            observer.next(response);
            observer.complete();
            return;
        }
        observer.error(new Error('No cached data available'));
    }

    private getStringEquipmentOfflineFallback(
        stringId: string,
        error: any,
    ): Observable<Response<StringEquipment>> {
        return new Observable((observer) => {
            this.offlineService.getStringEquipment(stringId).then((cached) => {
                if (cached) {
                    this.stringEquipmentState.store(cached).catch((err) => {
                        console.error('Error storing string equipment:', err);
                    });
                    observer.next(cached as any);
                    observer.complete();
                    return;
                }
                observer.error(error);
            });
        });
    }

    getWellProductionCategory(stringId: string) {
        const url = `${environment.apiURL}/v1/WellProductionStatus/GetWellProductionCategories?search[WellStringId]=${stringId}`;

        // If offline, return cached data immediately
        if (!this._isOnline.value) {
            return new Observable<Response<PaginateResponse<WellStringStatus>>>(
                (observer) => {
                    this.offlineService.getWellStringStatus(stringId).then(
                        (cached) =>
                            this.handleCachedWellStringStatus(
                                cached,
                                observer,
                                stringId,
                            ),
                        (err) => observer.error(err),
                    );
                },
            );
        }

        return this._httpClient
            .get<Response<PaginateResponse<WellStringStatus>>>(url)
            .pipe(
                tap((res) => {
                    if (res.data?.data && Array.isArray(res.data.data)) {
                        const firstItem = first(res.data.data);
                        if (firstItem) {
                            this.offlineService
                                .saveWellStringStatus(firstItem)
                                .catch((err) =>
                                    console.error(
                                        'Error caching well string status:',
                                        err,
                                    ),
                                );
                        }
                    }
                }),
                this.wellStringStatusState.resolveStore((res) => {
                    this._wellProdCategory.next(first(res.data.data));
                    return first(res.data.data);
                }),
                catchError((error) => {
                    if (!this._isOnline.value) {
                        return this.getWellStringStatusOfflineFallback(
                            stringId,
                            error,
                        );
                    }
                    return throwError(error);
                }),
            );
    }

    private handleCachedWellStringStatus(
        cached: WellStringStatus | undefined,
        observer: Observer<Response<PaginateResponse<WellStringStatus>>>,
        stringId: string,
    ) {
        if (cached) {
            const response: Response<PaginateResponse<WellStringStatus>> = {
                data: {
                    data: [cached],
                    count: 1,
                    pageIndex: 1,
                    pageSize: 1,
                },
                isSuccess: true,
                message: 'Loaded from cache',
                statusCode: '200',
            } as unknown as Response<PaginateResponse<WellStringStatus>>;
            // Resolve the wellStringStatusState with cached data
            this.wellStringStatusState.store(cached).catch((err) => {
                console.error('Error storing well string status:', err);
            });
            this._wellProdCategory.next(cached);
            observer.next(response);
            observer.complete();
            return;
        }
        observer.error(new Error('No cached data available'));
    }

    private getWellStringStatusOfflineFallback(
        stringId: string,
        error: any,
    ): Observable<Response<PaginateResponse<WellStringStatus>>> {
        return new Observable((observer) => {
            this.offlineService.getWellStringStatus(stringId).then((cached) => {
                if (cached) {
                    this.wellStringStatusState.store(cached).catch((err) => {
                        console.error('Error storing well string status:', err);
                    });
                    this._wellProdCategory.next(cached);
                    observer.next(cached as any);
                    observer.complete();
                    return;
                }
                observer.error(error);
            });
        });
    }

    getValveOptionType(): Observable<Response<OptionItem[]>> {
        const url = `${environment.apiURL}/v1/Option/GetOptionItems?mdOptionName=md_sce_valve_type`;

        // If offline, return cached data immediately
        if (!this._isOnline.value) {
            return new Observable<Response<OptionItem[]>>((observer) => {
                this.offlineService
                    .getValveOptionType()
                    .then((cached) => {
                        if (cached && cached.length > 0) {
                            observer.next({
                                data: cached,
                                isSuccess: true,
                                message: 'Loaded from cache',
                                statusCode: '200',
                            } as unknown as Response<OptionItem[]>);
                            observer.complete();
                            return;
                        }
                        observer.error(new Error('No cached data available'));
                    })
                    .catch((err) => observer.error(err));
            });
        }

        return this._httpClient.get<Response<OptionItem[]>>(url).pipe(
            take(1),
            tap((response) => {
                if (response.data) {
                    this.offlineService
                        .saveValveOptionType(response.data)
                        .catch((err) =>
                            console.error(
                                'Error caching valve option type:',
                                err,
                            ),
                        );
                }
            }),
            this.valveOptionTypeState.resolveStore((response) => {
                return { items: response.data };
            }),
            catchError((error) => {
                if (!this._isOnline.value) {
                    return new Observable<Response<OptionItem[]>>(
                        (observer) => {
                            this.offlineService
                                .getValveOptionType()
                                .then((cached) => {
                                    if (cached && cached.length > 0) {
                                        observer.next({
                                            data: cached,
                                            isSuccess: true,
                                            message: 'Loaded from cache',
                                            statusCode: '200',
                                        } as Response<OptionItem[]>);
                                        observer.complete();
                                        return;
                                    }
                                    observer.error(error);
                                });
                        },
                    );
                }
                return throwError(error);
            }),
        );
    }

    getSSSVOptionType(): Observable<OptionItem[]> {
        const url = `${environment.apiURL}/v1/Option/GetOptionItems?mdOptionName=md_sssv_type`;

        // If offline, return cached data immediately
        if (!this._isOnline.value) {
            return new Observable<OptionItem[]>((observer) => {
                this.offlineService
                    .getSSSVOptionType()
                    .then((cached) => {
                        if (cached && cached.length > 0) {
                            observer.next(cached);
                            observer.complete();
                            return;
                        }
                        observer.error(new Error('No cached data available'));
                    })
                    .catch((err) => observer.error(err));
            });
        }

        return this._httpClient.get<Response<OptionItem[]>>(url).pipe(
            take(1),
            tap((response) => {
                if (response.data) {
                    this.offlineService
                        .saveSSSVOptionType(response.data)
                        .catch((err) =>
                            console.error(
                                'Error caching SSSV option type:',
                                err,
                            ),
                        );
                }
            }),
            map((response) => {
                return response.data;
            }),
            catchError((error) => {
                if (!this._isOnline.value) {
                    return new Observable<OptionItem[]>((observer) => {
                        this.offlineService
                            .getSSSVOptionType()
                            .then((cached) => {
                                if (cached && cached.length > 0) {
                                    observer.next(cached);
                                    observer.complete();
                                    return;
                                }
                                observer.error(error);
                            });
                    });
                }
                return throwError(error);
            }),
        );
    }

    getWellString(id: string) {
        const url = composeUrl(
            `/v1/WellHeaderInformation/Strings/GetWellString/${id}`,
        );

        // If offline, return cached data immediately
        if (!this._isOnline.value) {
            return new Observable<Response<WellString>>((observer) => {
                this.offlineService
                    .getWellString(id)
                    .then((cached) => {
                        if (cached) {
                            observer.next({
                                data: cached,
                                isSuccess: true,
                                message: 'Loaded from cache',
                                statusCode: '200',
                            } as unknown as Response<WellString>);
                            observer.complete();
                            return;
                        }
                        observer.error(new Error('No cached data available'));
                    })
                    .catch((err) => observer.error(err));
            });
        }

        return this._httpClient.get<Response<WellString>>(url).pipe(
            take(1),
            tap((response) => {
                if (response.data) {
                    this.offlineService
                        .saveWellString(response.data)
                        .catch((err) =>
                            console.error('Error caching well string:', err),
                        );
                }
            }),
            this.wellStringState.resolveStore((response) => {
                return response.data;
            }),
            catchError((error) => {
                if (!this._isOnline.value) {
                    return new Observable((observer) => {
                        this.offlineService.getWellString(id).then((cached) => {
                            if (cached) {
                                observer.next({
                                    data: cached,
                                    isSuccess: true,
                                    message: 'Loaded from cache',
                                    statusCode: 200,
                                } as any);
                                observer.complete();
                                return;
                            }
                            observer.error(error);
                        });
                    });
                }
                return throwError(error);
            }),
        );
    }

    downloadExcel() {
        const url = composeUrl(
            `/v1/PreventiveMaintenance/Pm/DownloadOverallCdftExcel`,
        );
        this._httpClient
            .get(url, { responseType: 'blob' })
            .subscribe((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'cdft.xlsx';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            });
    }
}
